import { OverlayRef } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewEncapsulation,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
  viewChild
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { MODAL_CONFIG, ModalRef } from './modal.service';
import { modalBodyAnimation, modalBackdropAnimation } from '../animations/index';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import { deepActiveElement, focusInitial, restoreFocus, trapTabKey } from './focus-trap';

/** Dialog widths: confirm, form, tables, side-by-side comparison. */
export type DialogSize = 'sm' | 'md' | 'lg' | 'wide';

let nextTitleId = 0;

export class TemplateModalOverlayRef implements ModalRef<unknown> {
  public id: string;
  public componentInstance?: unknown;

  private readonly _afterClosed = new Subject<unknown>();
  private readonly _afterOpened = new Subject<void>();
  private readonly _isClosing = signal(false);
  readonly isClosing = this._isClosing.asReadonly();
  private _closeResult: unknown;
  private _disposed = false;

  constructor(
    private overlayRef: OverlayRef,
    id: string
  ) {
    this.id = id;
    setTimeout(() => this._afterOpened.next());
  }

  close(result?: unknown): void {
    if (this._disposed || this._isClosing()) return;
    this._closeResult = result;
    this._isClosing.set(true);
    // Safety fallback: dispose if animation done callback doesn't fire
    setTimeout(() => this.finishClose(), 300);
  }

  finishClose(): void {
    if (this._disposed) return;
    this._disposed = true;
    this._afterClosed.next(this._closeResult);
    this._afterClosed.complete();
    this._afterOpened.complete();
    this.overlayRef.dispose();
  }

  afterClosed(): Observable<unknown> {
    return this._afterClosed.asObservable();
  }

  afterOpened(): Observable<void> {
    return this._afterOpened.asObservable();
  }
}

/**
 * Dialog shell. Projects `.if-modal-title`, `.if-modal-content` and
 * `.if-modal-actions`; keeps Tab inside the dialog, closes on Esc and backdrop
 * click (unless `disableClose`), and returns focus to the opener on close.
 * An element marked `data-autofocus` receives focus when the dialog opens;
 * when it is disabled, the first control of the content does.
 */
@Component({
    selector: 'app-modal',
    imports: [TooltipDirective],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './modal.component.html',
    styleUrl: './modal.component.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
      class: 'if-shared-modal',
      '(document:keydown.escape)': 'onEscapeKey()'
    },
    animations: [
        modalBodyAnimation(),
        modalBackdropAnimation()
    ]
})
export class ModalComponent implements OnDestroy {
  private readonly overlayRef = inject(TemplateModalOverlayRef, { optional: true });
  private readonly modalConfig = inject(MODAL_CONFIG, { optional: true });
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Fixed width; unset keeps the content-sized dialog. */
  readonly size = input<DialogSize | null>(null);
  /** `alertdialog` for confirmations that interrupt and need an answer. */
  readonly dialogRole = input<'dialog' | 'alertdialog'>('dialog');
  /** Ids of the elements that describe the dialog, space separated. */
  readonly describedBy = input<string | null>(null);

  private readonly body = viewChild.required<ElementRef<HTMLElement>>('body');
  /** The dialog's name: the title block's heading, or the whole block when it has none. */
  readonly titleId = signal<string | null>(null);
  /** The title block's subheading, which describes the dialog. */
  private readonly subtitleId = signal<string | null>(null);
  readonly descriptionIds = computed(() => {
    const ids = [this.subtitleId(), this.describedBy()].filter((id): id is string => !!id);
    return ids.length ? ids.join(' ') : null;
  });
  private titleObserver: MutationObserver | null = null;

  private readonly opener: HTMLElement | null = deepActiveElement();

  showCloseButton = computed(() => !this.modalConfig?.hideCloseButton);
  animState = computed(() => this.overlayRef?.isClosing() ? 'closing' : 'open');

  constructor() {
    afterNextRender(() => {
      const body = this.body().nativeElement;
      const title = body.querySelector<HTMLElement>('.if-modal-title');
      if (title) {
        this.linkTitle(title);
        // Headings and subheadings that appear once data loads are linked too.
        if (typeof MutationObserver !== 'undefined') {
          this.titleObserver = new MutationObserver(() => this.linkTitle(title));
          this.titleObserver.observe(title, { childList: true, subtree: true });
        }
      }
      focusInitial(body, body, body.querySelector('.if-modal-content'));
    });
  }

  ngOnDestroy(): void {
    this.titleObserver?.disconnect();
    restoreFocus(this.opener);
  }

  private linkTitle(title: HTMLElement): void {
    const heading = title.querySelector<HTMLElement>('.if-modal-title-heading, h1, h2, h3, h4') ?? title;
    if (!heading.id) heading.id = `if-modal-title-${nextTitleId++}`;
    this.titleId.set(heading.id);
    const subtitle = title.querySelector<HTMLElement>('.if-modal-title-subheading');
    if (subtitle && !subtitle.id) subtitle.id = `if-modal-subtitle-${nextTitleId++}`;
    this.subtitleId.set(subtitle?.id ?? null);
  }

  onKeydown(event: KeyboardEvent): void {
    trapTabKey(event, this.body().nativeElement);
  }

  private get disableClose(): boolean {
    return this.modalConfig?.disableClose ?? false;
  }

  onBackdropClick(event: MouseEvent): void {
    if (!this.disableClose && event.target === event.currentTarget) {
      this.close();
    }
  }

  onEscapeKey(): void {
    if (!this.disableClose && this.isTopmost()) {
      this.close();
    }
  }

  /** Only the dialog opened last answers Esc when dialogs are stacked. */
  private isTopmost(): boolean {
    const all = document.querySelectorAll('.if-modal');
    const own = this.host.nativeElement.querySelector('.if-modal');
    return all.length === 0 || !own || all[all.length - 1] === own;
  }

  close(): void {
    this.overlayRef?.close();
  }

  onBodyAnimationDone(event: { toState: string }): void {
    if (event.toState === 'closing') {
      this.overlayRef?.finishClose();
    }
  }
}
